/**
 * Shared reporting UI primitives.
 *
 * Built on the app's shadcn components (Card, Table) and its gray palette, so
 * reports look like the rest of Dottie. All charts live in report-charts.tsx on
 * the shadcn chart wrapper; nothing here draws data — these are layout,
 * tables, and the caveat/finding chrome.
 */
import { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Info, Shield } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import ResponsiveLayout from "@/components/layout/responsive-layout";

export const money = (cents: number) =>
  "$" + Math.round(cents / 100).toLocaleString("en-US");

export const formatStatus = (status: string) =>
  status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

export async function reportGet(url: string) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message ?? res.statusText);
  return res.json();
}

export function AdminReportGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user || user.role !== "admin") {
    return (
      <ResponsiveLayout title="Reports" subtitle="Management reporting">
        <div className="flex items-center justify-center py-12">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <CardTitle className="text-red-800">Access Denied</CardTitle>
              <CardDescription>
                Reporting is restricted to administrators. Several reports cover individual
                staff members' work, so they are not visible to the people they describe.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </ResponsiveLayout>
    );
  }
  return <>{children}</>;
}

export const WAITING_ON_LABEL: Record<string, string> = {
  us: "Waiting on us",
  client: "Waiting on client",
  agency: "Waiting on FMCSA",
  payment: "Waiting on payment",
  unknown: "Unknown",
};

export const WAITING_ON_CLASS: Record<string, string> = {
  us: "bg-blue-100 text-blue-800",
  client: "bg-amber-100 text-amber-800",
  agency: "bg-violet-100 text-violet-800",
  payment: "bg-rose-100 text-rose-800",
  unknown: "bg-gray-100 text-gray-700",
};

/** KPI tile matching the app's existing summary cards (see the payments page). */
export function StatCard({
  label, value, sub, tone,
}: { label: string; value: ReactNode; sub?: ReactNode; tone?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-500">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${tone ?? ""}`}>{value}</div>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

/** A titled section. Every report block sits in one so spacing matches the app. */
export function ReportCard({
  title, description, actions, children,
}: { title: string; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
          </div>
          {actions}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** A finding stated in words, with an action. Never leave the headline to be derived. */
export function Finding({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-6 flex flex-wrap items-center gap-3">
      <p className="text-sm text-amber-900 flex-1">{children}</p>
      {action}
    </div>
  );
}

/** Context the reader needs before the numbers make sense. */
export function ReportNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 mb-6 text-sm text-gray-600">
      {children}
    </div>
  );
}

/** Caveats travel in the API payload; this renders them so the UI cannot drop one. */
export function Caveats({ items }: { items: { code: string; detail: string; count?: number }[] }) {
  if (!items?.length) return null;
  return (
    <details className="mt-2 mb-6">
      <summary className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer inline-flex items-center gap-1.5">
        <Info className="h-3.5 w-3.5" />
        {items.length === 1 ? "One note" : `${items.length} notes`} about how these figures are calculated
      </summary>
      <div className="mt-3 text-sm text-gray-500 space-y-2 pl-5 border-l-2 border-gray-200 max-w-3xl">
        {items.map(c => (
          <p key={c.code}>
            {c.detail}
            {c.count !== undefined && <span className="text-gray-400"> ({c.count})</span>}
          </p>
        ))}
      </div>
    </details>
  );
}

export interface ReportColumn {
  key: string;
  header: string;
  align?: "left" | "right";
  render?: (row: any) => ReactNode;
}

/** Table built on the app's shadcn Table so borders, spacing and hover match. */
export function ReportTable({
  columns, rows, footer, onRowClick, emptyMessage,
}: {
  columns: ReportColumn[];
  rows: any[];
  footer?: Record<string, ReactNode>;
  onRowClick?: (row: any) => void;
  emptyMessage?: string;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map(c => (
            <TableHead key={c.key} className={c.align === "right" ? "text-right" : undefined}>
              {c.header}
            </TableHead>
          ))}
          {onRowClick && <TableHead className="w-8" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length + (onRowClick ? 1 : 0)} className="h-24 text-center">
              <div className="text-gray-700 font-medium">Nothing to show</div>
              <div className="text-sm text-gray-500 mt-1">
                {emptyMessage ?? "No records match the current filters."}
              </div>
            </TableCell>
          </TableRow>
        )}
        {rows.map((row, i) => (
          <TableRow key={i} className={onRowClick ? "cursor-pointer" : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}>
            {columns.map(c => (
              <TableCell key={c.key}
                className={c.align === "right" ? "text-right tabular-nums" : undefined}>
                {c.render ? c.render(row) : String(row[c.key] ?? "—")}
              </TableCell>
            ))}
            {onRowClick && <TableCell className="text-right text-gray-400">→</TableCell>}
          </TableRow>
        ))}
      </TableBody>
      {footer && rows.length > 0 && (
        <TableFooter>
          <TableRow>
            {columns.map(c => (
              <TableCell key={c.key}
                className={c.align === "right" ? "text-right tabular-nums" : undefined}>
                {footer[c.key] ?? ""}
              </TableCell>
            ))}
            {onRowClick && <TableCell />}
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
}
